import React, { useCallback, useEffect, useState } from 'react';
import { PercentIcon, DollarSignIcon, UserIcon, CalendarIcon, ShoppingCartIcon, CheckCircleIcon, FileTextIcon } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
import { StatsCard } from '../components/ui/StatsCard';
import { PageHeader } from '../components/ui/PageHeader';
import { Pagination } from '../components/ui/Pagination';
import { SortableTableHeader } from '../components/ui/SortableTableHeader';
import { useSortableData } from '../hooks/useSortableData';
import { supabaseAdmin } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { CommissionRecord, VendorCommissionConfig } from '../types';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
export function ComisionesPage() {
  const {
    user,
    addActivityLog
  } = useAuth();
  const {
    users
  } = useApp();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  const [activeTab, setActiveTab] = useState<'registros' | 'config'>('registros');
  const [records, setRecords] = useState<CommissionRecord[]>([]);
  const [configs, setConfigs] = useState<VendorCommissionConfig[]>([]);
  const [localConfigs, setLocalConfigs] = useState<Record<string, {
    venta: string;
    cobro: string;
  }>>({});
  const [isLoading, setIsLoading] = useState(true);
  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState<'all' | 'venta' | 'cobro'>('all');
  const [savingId, setSavingId] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;
  const activeVendors = users.filter(u => u.rolNombre !== 'Administrador' && u.activo && u.permisos?.['new-visit'] === true);
  useEffect(() => {
    loadData();
  }, [user]);
  // Auto-refresh when commission data changes in Supabase
  const loadDataCallback = useCallback(() => {
    loadData();
  }, []);
  useRealtimeRefresh(['commission_records', 'vendor_commissions'], loadDataCallback);
  useEffect(() => {
    if (isAdmin && users.length > 0) {
      const initialLocal: Record<string, {
        venta: string;
        cobro: string;
      }> = {};
      activeVendors.forEach(v => {
        const existing = configs.find(c => c.vendor_id === v.id);
        initialLocal[v.id] = {
          venta: existing ? String(existing.comision_venta_pct) : '0',
          cobro: existing ? String(existing.comision_cobro_pct) : '0'
        };
      });
      setLocalConfigs(initialLocal);
    }
  }, [users, configs, isAdmin]);
  const loadData = async () => {
    setIsLoading(true);
    try {
      // Load records
      let query = supabaseAdmin.from('commission_records').select('*').order('fecha', {
        ascending: false
      });
      if (!isAdmin && user) {
        query = query.eq('vendor_id', user.id);
      }
      const {
        data: recordsData
      } = await query;
      if (recordsData) setRecords(recordsData);
      // Load configs (admin only)
      if (isAdmin) {
        const {
          data: configsData
        } = await supabaseAdmin.from('vendor_commissions').select('*');
        if (configsData) {
          setConfigs(configsData);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };
  const handleSaveConfig = async (vendorId: string) => {
    setSavingId(vendorId);
    const venta = parseFloat(localConfigs[vendorId]?.venta || '0');
    const cobro = parseFloat(localConfigs[vendorId]?.cobro || '0');
    const {
      error
    } = await supabaseAdmin.from('vendor_commissions').upsert({
      vendor_id: vendorId,
      comision_venta_pct: venta,
      comision_cobro_pct: cobro,
      updated_at: new Date().toISOString()
    }, {
      onConflict: 'vendor_id'
    });
    if (!error) {
      const vendor = users.find(u => u.id === vendorId);
      addActivityLog('Editó comisiones', `Vendedor: ${vendor?.nombre || vendorId} | Venta: ${venta}% | Cobro: ${cobro}%`);
      setSavedId(vendorId);
      setTimeout(() => setSavedId(null), 2000);
      setConfigs(prev => {
        const existing = prev.find(c => c.vendor_id === vendorId);
        if (existing) {
          return prev.map(c => c.vendor_id === vendorId ? {
            ...c,
            comision_venta_pct: venta,
            comision_cobro_pct: cobro
          } : c);
        }
        return [...prev, {
          vendor_id: vendorId,
          comision_venta_pct: venta,
          comision_cobro_pct: cobro
        }];
      });
    }
    setSavingId(null);
  };
  // Filtering records
  const filteredRecords = records.filter(r => {
    if (dateFrom && r.fecha < dateFrom) return false;
    if (dateTo && r.fecha > dateTo + 'T23:59:59') return false;
    if (vendorFilter !== 'all' && r.vendor_id !== vendorFilter) return false;
    if (typeFilter !== 'all' && r.tipo !== typeFilter) return false;
    return true;
  });
  // Sorting records
  const {
    items: sortedRecords,
    requestSort,
    sortConfig
  } = useSortableData(filteredRecords, {
    fecha: r => new Date(r.fecha).getTime(),
    vendedor: r => r.vendor_nombre,
    cliente: r => r.cliente_nombre,
    tipo: r => r.tipo,
    montoBase: r => r.monto_base,
    porcentaje: r => r.porcentaje,
    comision: r => r.monto_comision
  });
  const finalRecords = sortConfig ? sortedRecords : [...sortedRecords].sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());
  // Pagination
  const totalPages = Math.ceil(finalRecords.length / ITEMS_PER_PAGE);
  const paginatedRecords = finalRecords.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  // Stats
  const totalComisiones = filteredRecords.reduce((sum, r) => sum + r.monto_comision, 0);
  const comisionesVenta = filteredRecords.filter(r => r.tipo === 'venta').reduce((sum, r) => sum + r.monto_comision, 0);
  const comisionesCobro = filteredRecords.filter(r => r.tipo === 'cobro').reduce((sum, r) => sum + r.monto_comision, 0);
  const vendedoresConComision = new Set(filteredRecords.map(r => r.vendor_id)).size;
  const formatCurrency = (amount: number) => new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 2
  }).format(amount);
  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString('es-DO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  return <div className="space-y-6" data-id="element-939">
      <PageHeader title="Comisiones" subtitle="Gestión de comisiones por venta y cobro" icon={<PercentIcon className="w-6 h-6" data-id="element-941" />} data-id="element-940" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 animate-stagger-1" data-id="element-942">
        <StatsCard title="Total Comisiones" value={formatCurrency(totalComisiones)} icon={<DollarSignIcon className="w-5 h-5" data-id="element-944" />} variant="success" data-id="element-943" />
        <StatsCard title="Por Ventas" value={formatCurrency(comisionesVenta)} icon={<ShoppingCartIcon className="w-5 h-5" data-id="element-946" />} variant="primary" data-id="element-945" />
        <StatsCard title="Por Cobros" value={formatCurrency(comisionesCobro)} icon={<DollarSignIcon className="w-5 h-5" data-id="element-948" />} variant="warning" data-id="element-947" />
        <StatsCard title="Vendedores Activos" value={vendedoresConComision} icon={<UserIcon className="w-5 h-5" data-id="element-950" />} variant="default" data-id="element-949" />
      </div>

      <Card padding="none" className="animate-stagger-2" data-id="element-951">
        <div className="flex border-b border-slate-200" data-id="element-952">
          <button onClick={() => setActiveTab('registros')} className={`px-6 py-4 text-sm font-bold border-b-2 transition-colors ${activeTab === 'registros' ? 'border-[#2952C4] text-[#2952C4] bg-blue-50/30' : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`} data-id="element-953">
            Registros de Comisiones
          </button>
          {isAdmin && <button onClick={() => setActiveTab('config')} className={`px-6 py-4 text-sm font-bold border-b-2 transition-colors ${activeTab === 'config' ? 'border-[#2952C4] text-[#2952C4] bg-blue-50/30' : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`} data-id="element-954">
              Configuración por Vendedor
            </button>}
        </div>

        {activeTab === 'registros' && <div className="p-6" data-id="element-955">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6" data-id="element-956">
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} leftIcon={<CalendarIcon className="w-4 h-4" data-id="element-958" />} data-id="element-957" />
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} leftIcon={<CalendarIcon className="w-4 h-4" data-id="element-960" />} data-id="element-959" />
              {isAdmin && <Select value={vendorFilter} onChange={e => setVendorFilter(e.target.value)} options={[{
            value: 'all',
            label: 'Todos los vendedores'
          }, ...activeVendors.map(v => ({
            value: v.id,
            label: v.nombre
          }))]} data-id="element-961" />}
              <Select value={typeFilter} onChange={e => setTypeFilter(e.target.value as any)} options={[{
            value: 'all',
            label: 'Todos los tipos'
          }, {
            value: 'venta',
            label: 'Ventas'
          }, {
            value: 'cobro',
            label: 'Cobros'
          }]} data-id="element-962" />
            </div>

            {/* Table */}
            {isLoading ? <div className="p-12 text-center" data-id="element-963">
                <div className="w-8 h-8 border-3 border-slate-200 border-t-[#2952C4] rounded-full animate-spin mx-auto mb-4" data-id="element-964" />
                <p className="text-slate-500" data-id="element-965">Cargando registros...</p>
              </div> : filteredRecords.length === 0 ? <div className="p-16 text-center border-2 border-dashed border-slate-200 rounded-2xl" data-id="element-966">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-967">
                  <FileTextIcon className="w-8 h-8 text-slate-400" data-id="element-968" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-969">
                  No hay registros
                </h3>
                <p className="text-slate-500 text-sm" data-id="element-970">
                  No se encontraron comisiones con los filtros aplicados
                </p>
              </div> : <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-971">
                <div className="overflow-x-auto" data-id="element-972">
                  <table className="w-full" data-id="element-973">
                    <thead data-id="element-974">
                      <tr className="bg-slate-50 border-b border-slate-200" data-id="element-975">
                        <SortableTableHeader label="Fecha" sortKey="fecha" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-976" />
                        {isAdmin && <SortableTableHeader label="Vendedor" sortKey="vendedor" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-977" />}
                        <SortableTableHeader label="Cliente" sortKey="cliente" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-978" />
                        <SortableTableHeader label="Tipo" sortKey="tipo" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-979" />
                        <SortableTableHeader label="Monto Base" sortKey="montoBase" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-980" />
                        <SortableTableHeader label="Porcentaje" sortKey="porcentaje" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-981" />
                        <SortableTableHeader label="Comisión" sortKey="comision" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-982" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100" data-id="element-983">
                      {paginatedRecords.map(record => <tr key={record.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-984">
                          <td className="px-5 py-4 text-sm text-slate-600" data-id="element-985">
                            {formatDate(record.fecha)}
                          </td>
                          {isAdmin && <td className="px-5 py-4 font-medium text-slate-900" data-id="element-986">
                              {record.vendor_nombre}
                            </td>}
                          <td className="px-5 py-4 text-sm text-slate-700" data-id="element-987">
                            {record.cliente_nombre}
                          </td>
                          <td className="px-5 py-4 text-center" data-id="element-988">
                            {record.tipo === 'venta' ? <Badge variant="info" size="sm" data-id="element-989">
                                Venta
                              </Badge> : <Badge variant="success" size="sm" data-id="element-990">
                                Cobro
                              </Badge>}
                          </td>
                          <td className="px-5 py-4 text-right text-sm text-slate-600" data-id="element-991">
                            {formatCurrency(record.monto_base)}
                          </td>
                          <td className="px-5 py-4 text-center font-medium text-slate-700" data-id="element-992">
                            {record.porcentaje}%
                          </td>
                          <td className="px-5 py-4 text-right font-bold text-emerald-600" data-id="element-993">
                            {formatCurrency(record.monto_comision)}
                          </td>
                        </tr>)}
                    </tbody>
                    <tfoot className="bg-slate-50 border-t border-slate-200" data-id="element-994">
                      <tr data-id="element-995">
                        <td colSpan={isAdmin ? 6 : 5} className="px-5 py-3 text-right text-sm font-bold text-slate-700" data-id="element-996">
                          Total en esta página:
                        </td>
                        <td className="px-5 py-3 text-right font-bold text-emerald-700" data-id="element-997">
                          {formatCurrency(paginatedRecords.reduce((s, r) => s + r.monto_comision, 0))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <div className="p-4 border-t border-slate-200" data-id="element-998">
                  <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={filteredRecords.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-999" />
                </div>
              </div>}
          </div>}

        {activeTab === 'config' && isAdmin && <div className="p-6" data-id="element-1000">
            <div className="mb-6" data-id="element-1001">
              <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-1002">
                Configuración de Porcentajes
              </h3>
              <p className="text-sm text-slate-500" data-id="element-1003">
                Establece el porcentaje de comisión que recibirá cada vendedor
                por sus ventas y cobros.
              </p>
            </div>

            <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-1004">
              <div className="overflow-x-auto" data-id="element-1005">
                <table className="w-full text-sm" data-id="element-1006">
                  <thead data-id="element-1007">
                    <tr className="bg-slate-50 border-b border-slate-200" data-id="element-1008">
                      <th className="px-5 py-4 text-left font-semibold text-slate-600 uppercase tracking-wider" data-id="element-1009">
                        Vendedor
                      </th>
                      <th className="px-5 py-4 text-center font-semibold text-slate-600 uppercase tracking-wider" data-id="element-1010">
                        % Comisión Venta
                      </th>
                      <th className="px-5 py-4 text-center font-semibold text-slate-600 uppercase tracking-wider" data-id="element-1011">
                        % Comisión Cobro
                      </th>
                      <th className="px-5 py-4 text-right font-semibold text-slate-600 uppercase tracking-wider" data-id="element-1012">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-1013">
                    {activeVendors.map(vendor => <tr key={vendor.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-1014">
                        <td className="px-5 py-4" data-id="element-1015">
                          <div className="flex items-center gap-3" data-id="element-1016">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold" data-id="element-1017">
                              {vendor.nombre.charAt(0).toUpperCase()}
                            </div>
                            <span className="font-medium text-slate-900" data-id="element-1018">
                              {vendor.nombre}
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-4" data-id="element-1019">
                          <div className="flex justify-center" data-id="element-1020">
                            <div className="relative w-28" data-id="element-1021">
                              <input type="number" min="0" max="100" step="0.1" value={localConfigs[vendor.id]?.venta || ''} onChange={e => setLocalConfigs(prev => ({
                          ...prev,
                          [vendor.id]: {
                            ...prev[vendor.id],
                            venta: e.target.value
                          }
                        }))} className="w-full pr-8 pl-3 py-2 text-center text-sm font-medium border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2952C4] focus:border-[#2952C4]" data-id="element-1022" />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold" data-id="element-1023">
                                %
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4" data-id="element-1024">
                          <div className="flex justify-center" data-id="element-1025">
                            <div className="relative w-28" data-id="element-1026">
                              <input type="number" min="0" max="100" step="0.1" value={localConfigs[vendor.id]?.cobro || ''} onChange={e => setLocalConfigs(prev => ({
                          ...prev,
                          [vendor.id]: {
                            ...prev[vendor.id],
                            cobro: e.target.value
                          }
                        }))} className="w-full pr-8 pl-3 py-2 text-center text-sm font-medium border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2952C4] focus:border-[#2952C4]" data-id="element-1027" />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold" data-id="element-1028">
                                %
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-right" data-id="element-1029">
                          <div className="flex items-center justify-end gap-3" data-id="element-1030">
                            {savedId === vendor.id && <span className="text-xs font-bold text-emerald-600 flex items-center gap-1 animate-in fade-in slide-in-from-right-2" data-id="element-1031">
                                <CheckCircleIcon className="w-4 h-4" data-id="element-1032" /> Guardado
                              </span>}
                            <Button size="sm" onClick={() => handleSaveConfig(vendor.id)} isLoading={savingId === vendor.id} data-id="element-1033">
                              Guardar
                            </Button>
                          </div>
                        </td>
                      </tr>)}
                    {activeVendors.length === 0 && <tr data-id="element-1034">
                        <td colSpan={4} className="px-5 py-8 text-center text-slate-500" data-id="element-1035">
                          No hay vendedores activos en el sistema.
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>}
      </Card>
    </div>;
}